import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

/**
 * /es/learn/ampa-tms-florida
 */

const AMPA_TRADEMARK =
  "Ampa y Ampa One son marcas registradas de Ampa Inc. Neuro Wellness TMS Centers of America es un proveedor independiente y no pertenece a Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "¿Dónde puedo obtener TMS Ampa en Miami?",
    answer:
      "Neuro Wellness TMS Centers of America ofrece TMS Ampa One autorizado por la FDA en Coral Springs, a unos 45 minutos en auto desde Miami. Los programas acelerados significan menos viajes, por lo que pacientes de todo el sur de Florida pueden completar el tratamiento en unos días. Llame al 954-827-9793 para programar.",
  },
  {
    question: "¿Dónde puedo obtener TMS Ampa en Fort Lauderdale?",
    answer:
      "Nuestra clínica de Coral Springs está a unos 25 minutos en auto desde Fort Lauderdale. Ofrecemos TMS Ampa One autorizado por la FDA con programas acelerados. Llame al 954-827-9793 para programar una consulta gratuita.",
  },
  {
    question: "¿Dónde puedo obtener TMS Ampa en Boca Raton?",
    answer:
      "Nuestra clínica de Coral Springs está a unos 25 minutos en auto desde Boca Raton. Ofrecemos TMS Ampa One autorizado por la FDA con programas acelerados. Llame al 954-827-9793 para programar una consulta gratuita.",
  },
  {
    question: "¿Dónde puedo obtener TMS Ampa en West Palm Beach?",
    answer:
      "Nuestra clínica de Coral Springs está a unos 50 minutos en auto desde West Palm Beach. Ofrecemos TMS Ampa One autorizado por la FDA con programas acelerados, por lo que el trayecto es manejable para un curso corto. Llame al 954-827-9793 para programar.",
  },
  {
    question: "¿Hay TMS Ampa One cerca de mí en Florida?",
    answer:
      "Neuro Wellness TMS Centers of America en Coral Springs ofrece TMS Ampa One autorizado por la FDA, ubicado de forma central para pacientes de Miami-Dade, Broward y Palm Beach. Para confirmar si una clínica usa Ampa One, consulte el localizador oficial de proveedores de Ampa en ampahealth.com/find-a-provider. Los programas acelerados permiten que pacientes de toda Florida y de fuera del estado completen el tratamiento en unos días.",
  },
];

export const esAmpaTmsFlorida = makeEsArticle({
  slug: "ampa-tms-florida",
  title:
    "TMS Ampa One en Florida: Miami, Fort Lauderdale, Boca Raton y Palm Beach",
  description:
    "Dónde obtener TMS Ampa One autorizado por la FDA en Florida. Tiempos de conducción desde Coral Springs a Miami, Fort Lauderdale, Boca Raton y Palm Beach, además de opciones de viaje y cómo verificar un proveedor.",
  h1: "Dónde obtener TMS Ampa One en Florida",
  breadcrumbLabel: "TMS Ampa One en Florida",
  shortAnswer:
    "Neuro Wellness TMS Centers of America ofrece TMS Ampa One autorizado por la FDA en Coral Springs, ubicado de forma central para pacientes de Miami-Dade, Broward y Palm Beach. Los programas acelerados significan menos viajes, por lo que pacientes de toda Florida y de fuera del estado pueden completar el tratamiento en unos días. Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica.",
  keyTakeaways: [
    "El TMS Ampa One se ofrece en Coral Springs, central para Miami-Dade, Broward y Palm Beach.",
    "Tiempos de conducción: Fort Lauderdale ~25 min, Boca Raton ~25 min, Miami ~45 min, West Palm Beach ~50 min.",
    "Los programas acelerados significan menos viajes: pacientes de toda Florida y de fuera del estado pueden terminar en unos días.",
    "Verifique que una clínica use Ampa One en ampahealth.com/find-a-provider (estamos listados).",
    "Disponibilidad de fin de semana; llame al 954-827-9793 para confirmar nuestro horario actual.",
  ],
  sections: [
    {
      heading: "Respuesta rápida",
      paragraphs: [
        "Neuro Wellness TMS Centers of America ofrece TMS Ampa One autorizado por la FDA en Coral Springs, ubicado de forma central para pacientes de Miami-Dade, Broward y Palm Beach. Los programas acelerados significan menos viajes, por lo que pacientes de toda Florida y de fuera del estado pueden completar el tratamiento en unos días en lugar de viajar durante 4–6 semanas.",
      ],
    },
    {
      heading: "Tiempos de conducción desde Coral Springs",
      paragraphs: [
        "Nuestra clínica está en 9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065. Tiempos aproximados de conducción (verifique con Google Maps antes de viajar):",
      ],
      table: {
        headers: ["Desde", "Tiempo de conducción a Coral Springs"],
        rows: [
          ["Fort Lauderdale", "~25 min"],
          ["Boca Raton", "~25 min"],
          ["Miami", "~45 min"],
          ["West Palm Beach", "~50 min"],
          ["Naples", "~1 h 45 min"],
          ["Tampa / Orlando", "~3 h"],
        ],
      },
    },
    {
      heading: "Si viene de más lejos",
      paragraphs: [
        "Los pacientes de fuera del sur de Florida pueden completar un curso acelerado de Ampa One en unos días en lugar de viajar durante semanas. Hoteles cercanos, un acompañante y el seguimiento en casa hacen que una estancia corta sea práctica. Consulte [[viajar para tratamiento|/es/learn/travel-for-saint-tms]] (la misma logística se aplica a Ampa One).",
      ],
    },
    {
      heading: "Cómo verificar si una clínica usa Ampa One",
      paragraphs: [
        "El localizador oficial de proveedores de Ampa en ampahealth.com/find-a-provider muestra qué clínicas están listadas, y nosotros estamos listados. No nombramos a otras clínicas. Si una clínica afirma ofrecer Ampa One, confírmelo en el localizador y pregunte si un psiquiatra supervisa su atención.",
      ],
    },
    {
      heading: "Nuestra ubicación, horario y disponibilidad de fin de semana",
      paragraphs: [
        "Neuro Wellness TMS Centers of America está en 9734 West Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065. Llame al 954-827-9793 para confirmar nuestro horario actual y la disponibilidad de fin de semana.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Localizador de proveedores. ampahealth.com/find-a-provider (consultado el 3 de noviembre de 2026).",
      url: "https://www.ampahealth.com/find-a-provider",
    },
    {
      citation:
        "Google Maps. Estimaciones de tiempo de conducción, Coral Springs, FL.",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Ampa One™ en Florida",
      href: "/es/ampa",
      description: "Tratamiento de depresión acelerado y guiado por cámara",
    },
    {
      title: "TMS de un día en Florida",
      href: "/es/one-day-tms",
      description: "El protocolo Ampa One Day explicado",
    },
    {
      title: "Costo del TMS Ampa One",
      href: "/es/learn/ampa-tms-cost",
      description: "Precio, seguro y opciones de pago",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/es/learn/saint-vs-ampa-one",
      description: "Dos opciones de TMS acelerado, una clínica en Florida",
    },
    {
      title: "Solicitar una consulta",
      href: "/es/contactus",
      description: "Reserve una consulta gratuita con nuestro equipo clínico",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  toc: [
    { label: "Respuesta rápida", href: "#respuesta-rapida" },
    {
      label: "Tiempos de conducción",
      href: "#tiempos-de-conduccion-desde-coral-springs",
    },
    { label: "Si viene de más lejos", href: "#si-viene-de-mas-lejos" },
    {
      label: "Verificar un proveedor",
      href: "#como-verificar-si-una-clinica-usa-ampa-one",
    },
    {
      label: "Ubicación y horario",
      href: "#nuestra-ubicacion-horario-y-disponibilidad-de-fin-de-semana",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica. Los efectos secundarios comunes incluyen molestias leves en el cuero cabelludo y dolor de cabeza que suelen desaparecer. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Obtenga TMS Ampa One en Florida",
    primaryText: "Solicitar una consulta gratuita",
    primaryHref: "/es/ampa",
    secondaryText: "Llamar 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} as EsArticleInput);
